import * as React from "react";

import { cn } from "../../lib/utils";
import { IconSelector } from "@tabler/icons-react";

type NativeSelectProps = Omit<React.ComponentProps<"select">, "size"> & {
  size?: "sm" | "default";
};

const nativeSelectSizes: Record<
  NonNullable<NativeSelectProps["size"]>,
  string
> = {
  sm: "h-6 py-0 text-xs leading-xs",
  default: "h-7 py-0.5 text-nav leading-nav",
};

const nativeSelectStyles = {
  wrapper: "relative w-fit",
  select:
    "w-full min-w-0 appearance-none select-none rounded-md border border-input bg-field pl-2.5 pr-8 text-fg outline-none disabled:opacity-50 focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
  icon: "pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 select-none text-muted-foreground",
  option: "bg-[Canvas] text-[CanvasText]",
};

function NativeSelect({
  className,
  size = "default",
  ...props
}: NativeSelectProps) {
  return (
    <div
      className={cn(nativeSelectStyles.wrapper, className)}
      data-slot="native-select-wrapper"
      data-size={size}
    >
      <select
        data-slot="native-select"
        data-size={size}
        className={cn(nativeSelectStyles.select, nativeSelectSizes[size])}
        {...props}
      />
      <IconSelector
        className={nativeSelectStyles.icon}
        aria-hidden="true"
        data-slot="native-select-icon"
      />
    </div>
  );
}

function NativeSelectOption({
  className,
  ...props
}: React.ComponentProps<"option">) {
  return (
    <option
      data-slot="native-select-option"
      className={cn(nativeSelectStyles.option, className)}
      {...props}
    />
  );
}

function NativeSelectOptGroup({
  className,
  ...props
}: React.ComponentProps<"optgroup">) {
  return (
    <optgroup
      data-slot="native-select-optgroup"
      className={cn(nativeSelectStyles.option, className)}
      {...props}
    />
  );
}

export { NativeSelect, NativeSelectOptGroup, NativeSelectOption };
